import React, { useEffect, useRef, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Icon from '../../../hive/ui/Icon';
import { useFairTracker } from '../data/FairTrackerStore';
import { preloadStrings, tr } from '../i18n';
import { LANGUAGES } from '../data/constants';
import { BASE, p, FT_ROUTES } from '../routes';

/**
 * Fair Tracker's context strip.
 *
 * NOT a second top bar. The Hive top bar sits above this and owns the app
 * title and the back arrow out to Hive — exactly as it does for every other
 * BeeNexa app. Duplicating a back button or the wordmark here would be two
 * chromes competing, so this strip carries only what Hive's bar cannot know
 * about: which fair is active, the language, Fair Tracker's own settings, and
 * (on desktop) its section nav.
 *
 * Gold, because under BeeNexa's dark-green top bar a second dark strip read as
 * more chrome and its controls were hard to pick out.
 */
export default function FairTopBar({ fairLabel, lang = 'EN' }) {
  const location = useLocation();
  const { settings, saveSettings } = useFairTracker();
  const [langOpen, setLangOpen] = useState(false);
  const menuRef = useRef(null);

  const current = (settings && settings.language) || 'en';

  /* Close the menu on an outside click or Escape — a dropdown pinned open
     behind the rest of the app is worse than no dropdown. */
  useEffect(() => {
    if (!langOpen) return undefined;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setLangOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setLangOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [langOpen]);

  const openLang = () => {
    // Fetch the translation chunk while the menu animates in, so picking a
    // language applies immediately instead of flashing English first.
    preloadStrings();
    setLangOpen((o) => !o);
  };

  const choose = async (code) => {
    setLangOpen(false);
    if (code === current) return;
    try {
      await saveSettings({ ...settings, language: code });
    } catch (e) {
      /* a failed preference write must not break navigation */
    }
  };

  const navItems = FT_ROUTES.filter((r) =>
    ['dashboard', 'suppliers', 'entry', 'plan', 'shortlist', 'followups'].includes(r.key),
  );

  return (
    <div className="ft-strip">
      <Link
        to={p('/fairs')}
        className="ft-fairchip"
        title={tr("Switch fair")}
        aria-label={fairLabel ? `Fair: ${fairLabel}. Switch fair` : 'Choose a fair'}
      >
        <Icon name="MapPin" size={13} />
        <span>{fairLabel || 'Choose a fair'}</span>
        <Icon name="ChevronDown" size={13} />
      </Link>

      <nav className="ft-topnav" aria-label={tr("Fair Tracker")}>
        {navItems.map((r) => {
          const on = r.exact ? location.pathname === r.path : location.pathname.startsWith(r.path);
          return (
            <Link key={r.key} to={r.path} className={on ? 'ft-topnav-on' : undefined}>
              {tr(r.key === 'dashboard' ? 'Home' : r.title)}
            </Link>
          );
        })}
      </nav>

      <span className="ft-topbar-spacer" />

      <div className="ft-langwrap" ref={menuRef}>
        <button
          type="button"
          className="ft-strip-btn"
          onClick={openLang}
          aria-haspopup="menu"
          aria-expanded={langOpen}
          title={tr("Language")}
        >
          <Icon name="Globe" size={16} />
          <span>{lang}</span>
          <Icon name={langOpen ? 'ChevronUp' : 'ChevronDown'} size={12} />
        </button>

        {langOpen && (
          <ul className="ft-langmenu" role="menu" aria-label={tr("Language")}>
            {LANGUAGES.map(([code, name, flag]) => (
              <li key={code}>
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={code === current}
                  className={code === current ? 'ft-langmenu-on' : undefined}
                  onClick={() => choose(code)}
                >
                  <span className="ft-langflag" aria-hidden="true">
                    {flag}
                  </span>
                  <span className="ft-langname">{name}</span>
                  {code === current && <Icon name="Check" size={15} />}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Link to={p('/settings')} className="ft-strip-btn" title={tr("Fair Tracker settings")} aria-label={tr("Settings")}>
        <Icon name="Settings" size={16} />
      </Link>
    </div>
  );
}

export { BASE };
